import React, { useEffect, useState } from 'react';
import api from '../../config/axios';
import './EmployeeManagement.css';
import Header from '../common/Header';
import { useNavigate } from 'react-router-dom';

interface Employee {
  _id: string;
  username: string;
  email: string;
  role: string;
  isActive: boolean;
  employmentType?: 'fulltime' | 'parttime';
  baseSalary: number;
  hourlyRate: number;
  commissionRate: number;
  createdAt: string;
}

interface EmployeeManagementProps {
  user: any;
}

const EmployeeManagement: React.FC<EmployeeManagementProps> = ({ user }) => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editEmployee, setEditEmployee] = useState<Employee | null>(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    employmentType: '',
    baseSalary: '',
    hourlyRate: '',
    commissionRate: ''
  });

  const navigate = useNavigate();

  const fetchEmployees = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/api/employees');
      setEmployees(res.data.data);
    } catch (err: any) {
      setError('Không thể tải danh sách nhân viên');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  const openForm = (employee?: Employee) => {
    if (employee) {
      setEditEmployee(employee);
      setFormData({ 
        username: employee.username, 
        email: employee.email, 
        password: '',
        employmentType: employee.employmentType || '',
        baseSalary: employee.baseSalary?.toString() || '',
        hourlyRate: employee.hourlyRate?.toString() || '',
        commissionRate: employee.commissionRate?.toString() || ''
      });
    } else {
      setEditEmployee(null);
      setFormData({ 
        username: '', 
        email: '', 
        password: '',
        employmentType: '',
        baseSalary: '',
        hourlyRate: '',
        commissionRate: ''
      });
    }
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditEmployee(null);
    setFormData({ 
      username: '', 
      email: '', 
      password: '',
      employmentType: '',
      baseSalary: '',
      hourlyRate: '',
      commissionRate: ''
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const submitData = {
        ...formData,
        employmentType: formData.employmentType || undefined,
        baseSalary: Number(formData.baseSalary) || 0,
        hourlyRate: Number(formData.hourlyRate) || 0,
        commissionRate: Number(formData.commissionRate) || 0
      };
      
      if (editEmployee) {
        await api.put(`/api/employees/${editEmployee._id}`, submitData);
      } else {
        await api.post('/api/employees', submitData);
      }
      fetchEmployees();
      closeForm();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Lỗi khi lưu nhân viên');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Bạn chắc chắn muốn xóa nhân viên này?')) return;
    setLoading(true);
    setError('');
    try {
      await api.delete(`/api/employees/${id}`);
      fetchEmployees();
    } catch (err: any) {
      setError('Lỗi khi xóa nhân viên');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return amount.toLocaleString('vi-VN') + ' ₫';
  };

  const onLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/';
  };

  return (
    <>
      <Header user={user} onLogout={onLogout}>
        <span className="user-role">{user.role === 'admin' ? 'Quản lý' : user.role}</span>
      </Header>
      <div className="employee-container">
        <div className="employee-header">
          <button
            className="employee-back-btn"
            onClick={() => navigate('/')}
            title="Quay lại Dashboard"
            style={{
              background: 'none',
              border: 'none',
              fontSize: 28,
              cursor: 'pointer',
              marginRight: 12
            }}
          >
            ←
          </button>
          <span className="employee-title">QUẢN LÝ NHÂN VIÊN</span>
          <button className="employee-add-btn" onClick={() => openForm()}>+ Thêm nhân viên</button>
        </div>
        {loading && <div>Đang tải...</div>}
        {error && <div className="error-message">{error}</div>}
        <div className="employee-table-wrap">
        <table className="employee-table">
          <thead>
            <tr>
              <th>Tên đăng nhập</th>
              <th>Email</th>
              <th>Hình thức</th>
              <th>Lương cơ bản</th>
              <th>Lương/giờ</th>
              <th>Hoa hồng (%)</th>
              <th>Ngày tạo</th>
              <th>Hành động</th>
            </tr>
          </thead>
          <tbody>
            {employees.map(emp => (
              <tr key={emp._id}>
                <td>{emp.username}</td>
                <td>{emp.email}</td>
                <td>{emp.employmentType === 'fulltime' ? 'Fulltime' : emp.employmentType === 'parttime' ? 'Parttime' : <span className="employee-missing">Chưa chọn</span>}</td>
                <td>{formatCurrency(emp.baseSalary || 0)}</td>
                <td>{formatCurrency(emp.hourlyRate || 0)}</td>
                <td>{emp.commissionRate || 0}%</td>
                <td>{new Date(emp.createdAt).toLocaleString()}</td>
                <td className="employee-actions">
                  <button className="edit-btn" onClick={() => openForm(emp)}>Sửa</button>
                  <button className="delete-btn" onClick={() => handleDelete(emp._id)}>Xóa</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {showForm && (
          <div className="employee-form-modal">
            <form className="employee-form" onSubmit={handleSubmit}>
              <div className="employee-form-title">{editEmployee ? 'Sửa nhân viên' : 'Thêm nhân viên'}</div>
              <div className="employee-form-group">
                <label>Tên đăng nhập:</label>
                <input className="employee-form-input" type="text" name="username" value={formData.username} onChange={handleChange} required />
              </div>
              <div className="employee-form-group">
                <label>Email:</label>
                <input className="employee-form-input" type="email" name="email" value={formData.email} onChange={handleChange} required />
              </div>
              <div className="employee-form-group">
                <label>Mật khẩu{editEmployee ? ' (để trống nếu không đổi)' : ''}:</label>
                <input className="employee-form-input" type="password" name="password" value={formData.password} onChange={handleChange} minLength={editEmployee ? 0 : 6} />
              </div>
              <div className="employee-form-group">
                <label>Hình thức làm việc:</label>
                <select className="employee-form-input" name="employmentType" value={formData.employmentType} onChange={handleChange}>
                  <option value="">— Chọn hình thức —</option>
                  <option value="fulltime">Fulltime (lương theo lương cơ bản)</option>
                  <option value="parttime">Parttime (lương theo giờ)</option>
                </select>
                <small className="employee-form-hint">Bắt buộc chọn trước khi tính lương tháng.</small>
              </div>
              <div className="employee-form-group">
                <label>Lương cơ bản (₫):</label>
                <input className="employee-form-input" type="number" name="baseSalary" value={formData.baseSalary} onChange={handleChange} min="0" />
              </div>
              <div className="employee-form-group">
                <label>Lương theo giờ (₫/h):</label>
                <input className="employee-form-input" type="number" name="hourlyRate" value={formData.hourlyRate} onChange={handleChange} min="0" />
              </div>
              <div className="employee-form-group">
                <label>Tỷ lệ hoa hồng (%):</label>
                <input className="employee-form-input" type="number" name="commissionRate" value={formData.commissionRate} onChange={handleChange} min="0" max="100" step="0.1" />
              </div>
              <div className="employee-form-actions">
                <button type="submit" className="employee-form-save-btn" disabled={loading}>{loading ? 'Đang lưu...' : 'Lưu'}</button>
                <button type="button" className="employee-form-cancel-btn" onClick={closeForm}>Hủy</button>
              </div>
              {error && <div className="error-message">{error}</div>}
            </form>
          </div>
        )}
      </div>
    </>
  );
};

export default EmployeeManagement; 